import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { OptiBaySessionVerification } from '@/types/optibay';

const sb = supabase as any;

export function useSessionVerifications(sessionId: string | undefined) {
  return useQuery({
    queryKey: ['optibay-session-post-verifications', sessionId],
    queryFn: async (): Promise<OptiBaySessionVerification[]> => {
      if (!sessionId) return [];
      const { data, error } = await sb
        .from('optibay_session_verifications')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as OptiBaySessionVerification[];
    },
    enabled: !!sessionId,
  });
}

export interface AddPostVerificationParams {
  sessionId: string;
  repairId?: string;
  verificationMethod: string;
  notes?: string;
}

export function useAddPostVerification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: AddPostVerificationParams) => {
      const { data, error } = await sb
        .from('optibay_session_verifications')
        .insert({
          session_id: params.sessionId,
          repair_id: params.repairId || null,
          verification_method: params.verificationMethod,
          result: 'pending',
          notes: params.notes || null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as OptiBaySessionVerification;
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-post-verifications', data.session_id] });
    },
  });
}

export function useUpdatePostVerification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: {
      id: string;
      updates: Partial<Pick<OptiBaySessionVerification, 'result' | 'notes' | 'verified_at' | 'repair_verified' | 'symptom_resolved' | 'comeback_flag'>>;
    }) => {
      const { data, error } = await sb
        .from('optibay_session_verifications')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as OptiBaySessionVerification;
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-post-verifications', data.session_id] });
    },
  });
}

export function useRemovePostVerification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, sessionId }: { id: string; sessionId: string }) => {
      const { error } = await sb
        .from('optibay_session_verifications')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { sessionId };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-post-verifications', result.sessionId] });
    },
  });
}
